import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, InputBase, MenuItem, Skeleton, Stack, TextField, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { grey } from "@mui/material/colors";
import { services } from "data/authData";
import { GlobalState } from "context/GlobalContext";
import { gigaExplore, gigaListAdsAccounts } from "./gigaApi";
import GigaLogs from "./GigaLogs";

const labelSx = {
	fontSize: 11,
	fontWeight: 700,
	letterSpacing: 0.3,
	color: grey[600],
	mb: 0.5,
	display: "block",
};

const fieldSx = {
	"& .MuiOutlinedInput-root": {
		bgcolor: "#fff",
		borderRadius: 1.5,
		fontSize: 13.5,
		height: 40,
	},
};

const commitToken = (raw, list) => {
	const next = String(raw || "")
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean)
		.filter((item) => !list.some((existing) => existing.toLowerCase() === item.toLowerCase()));
	return next.length ? [...list, ...next] : list;
};

const Giga = () => {
	const navigate = useNavigate();
	const { snackBar, setSnackBar, setSnackBarOpen } = GlobalState();
	const [seedList, setSeedList] = useState(["sneakers", "running shoes"]);
	const [seedDraft, setSeedDraft] = useState("");
	const [runName, setRunName] = useState("");
	const [country, setCountry] = useState("India");
	const [language, setLanguage] = useState("English");
	const [brandName, setBrandName] = useState("Rare Rabbit");
	const [adsAccounts, setAdsAccounts] = useState([]);
	const [customerId, setCustomerId] = useState("");
	const [adsLoading, setAdsLoading] = useState(true);
	const [running, setRunning] = useState(false);
	const [runsGridKey, setRunsGridKey] = useState(0);

	const notify = (message, severity = "error") => {
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
		setSnackBarOpen(true);
	};

	const reloadRuns = () => setRunsGridKey((key) => key + 1);

	const loadAdsAccounts = async () => {
		setAdsLoading(true);
		try {
			const list = await gigaListAdsAccounts();
			const accounts = Array.isArray(list) ? list : [];
			setAdsAccounts(accounts);
			setCustomerId((current) => current || accounts[0]?.customerId || "");
		} catch (error) {
			console.error(error);
		} finally {
			setAdsLoading(false);
		}
	};

	useEffect(() => {
		loadAdsAccounts();
	}, []);

	const openRun = (id) => {
		if (!id) return;
		navigate(`/explore/${services.giga.path}/run/${id}`);
	};

	const addSeeds = () => {
		setSeedList((prev) => commitToken(seedDraft, prev));
		setSeedDraft("");
	};

	const handleRun = async () => {
		const name = String(runName || "").trim();
		if (!name) {
			notify("Enter a unique run name.");
			return;
		}
		const seeds = commitToken(seedDraft, seedList);
		if (!seeds.length) {
			notify("Add at least one keyword.");
			return;
		}
		if (!customerId) {
			notify("Select an Ads account.");
			return;
		}
		setSeedList(seeds);
		setSeedDraft("");
		setRunning(true);
		try {
			const result = await gigaExplore({ name, seeds, country, language, brandName, customerId, limit: 5000 });
			setRunName("");
			reloadRuns();
			notify("Run created.", "success");
			openRun(result._id);
		} catch (error) {
			notify(error.response?.data?.message || error.message);
			reloadRuns();
		} finally {
			setRunning(false);
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 60px)",
				bgcolor: grey[50],
				px: { xs: 1.5, md: 3 },
				py: { xs: 1.5, md: 2 },
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}>
			<Box
				sx={{
					width: "100%",
					maxWidth: 1400,
					mx: "auto",
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
				}}>
				<Box sx={{ mb: 1.5, flexShrink: 0 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: 600,
							fontSize: { xs: 24, md: 28 },
							letterSpacing: "-0.02em",
							color: grey[900],
							lineHeight: 1.25,
						}}>
						GIGA
					</Typography>
					<Typography variant="body2" sx={{ color: grey[600], mt: 0.5, fontSize: 15 }}>
						Create a keyword research run, then open it for insights and campaigns.
					</Typography>
				</Box>

				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						border: `1px solid ${grey[200]}`,
						borderRadius: 2,
						overflow: "hidden",
						bgcolor: "#fff",
					}}>
					<Box sx={{ px: 1.5, pt: 1.25, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${grey[200]}` }}>
						<Stack direction="row" spacing={1.25} alignItems="flex-end">
							<Box sx={{ width: 180, flexShrink: 0 }}>
								<Typography sx={labelSx}>Name</Typography>
								<TextField
									size="small"
									hiddenLabel
									value={runName}
									onChange={(e) => setRunName(e.target.value)}
									placeholder="Unique run name"
									fullWidth
									sx={fieldSx}
								/>
							</Box>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography sx={labelSx}>Keywords</Typography>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										px: 1,
										height: 40,
										border: `1px solid ${grey[300]}`,
										borderRadius: 1.5,
										bgcolor: "#fff",
										overflow: "hidden",
										"&:hover": { borderColor: grey[400] },
										"&:focus-within": { borderColor: "primary.main", borderWidth: 1 },
									}}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											flex: 1,
											minWidth: 0,
											overflowX: "auto",
											scrollbarWidth: "none",
											"&::-webkit-scrollbar": { display: "none" },
										}}>
										{seedList.map((seed) => (
											<Chip
												key={seed}
												size="small"
												label={seed}
												onDelete={() => setSeedList((prev) => prev.filter((item) => item !== seed))}
												sx={{
													height: 24,
													flexShrink: 0,
													bgcolor: grey[50],
													border: `1px solid ${grey[200]}`,
													fontWeight: 600,
													"& .MuiChip-label": { px: 0.9, fontSize: 12.5 },
												}}
											/>
										))}
										<InputBase
											value={seedDraft}
											onChange={(e) => setSeedDraft(e.target.value)}
											onBlur={addSeeds}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === ",") {
													e.preventDefault();
													addSeeds();
												}
												if (e.key === "Backspace" && !seedDraft && seedList.length) {
													setSeedList((prev) => prev.slice(0, -1));
												}
											}}
											placeholder={seedList.length ? "Add another" : "e.g. sneakers"}
											sx={{ flex: 1, minWidth: 96, fontSize: 13.5 }}
										/>
									</Box>
								</Box>
							</Box>

							<Box sx={{ width: 128, flexShrink: 0 }}>
								<Typography sx={labelSx}>Country</Typography>
								<TextField
									size="small"
									hiddenLabel
									value={country}
									onChange={(e) => setCountry(e.target.value)}
									fullWidth
									sx={fieldSx}
								/>
							</Box>
							<Box sx={{ width: 128, flexShrink: 0 }}>
								<Typography sx={labelSx}>Language</Typography>
								<TextField
									size="small"
									hiddenLabel
									value={language}
									onChange={(e) => setLanguage(e.target.value)}
									fullWidth
									sx={fieldSx}
								/>
							</Box>
							<Box sx={{ width: 168, flexShrink: 0 }}>
								<Typography sx={labelSx}>Ads account</Typography>
								{adsLoading ? (
									<Skeleton variant="rounded" height={40} sx={{ borderRadius: 1.5 }} />
								) : (
									<TextField
										select
										size="small"
										hiddenLabel
										value={customerId}
										onChange={(e) => setCustomerId(e.target.value)}
										fullWidth
										disabled={!adsAccounts.length}
										sx={fieldSx}>
										{!adsAccounts.length && (
											<MenuItem value="">
												<em>Not configured</em>
											</MenuItem>
										)}
										{adsAccounts.map((account) => (
											<MenuItem key={account.customerId} value={account.customerId}>
												{account.name}
											</MenuItem>
										))}
									</TextField>
								)}
							</Box>
							<Box sx={{ width: 148, flexShrink: 0 }}>
								<Typography sx={labelSx}>Brand</Typography>
								<TextField
									size="small"
									hiddenLabel
									value={brandName}
									onChange={(e) => setBrandName(e.target.value)}
									fullWidth
									sx={fieldSx}
								/>
							</Box>
							<Button
								variant="contained"
								disableElevation
								startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
								onClick={handleRun}
								disabled={running || adsLoading || !customerId || !String(runName || "").trim()}
								sx={{
									height: 40,
									px: 2,
									flexShrink: 0,
									textTransform: "none",
									fontWeight: 700,
									borderRadius: 1.5,
									whiteSpace: "nowrap",
								}}>
								Run
							</Button>
						</Stack>
						<Typography sx={{ mt: 0.75, fontSize: 12, color: grey[500] }}>
							{adsLoading
								? "Loading Ads accounts…"
								: adsAccounts.length
								? "Each run needs a unique name. One Ads account per run. Enter to add a keyword. Run fills Explore; Insights and Campaigns stay empty until you generate them."
								: "Add Ads accounts in gigaConfig.js (or GIGA_ADS_ACCOUNTS), then pick one and Run."}
						</Typography>
					</Box>

					<GigaLogs key={runsGridKey} onSelect={openRun} />
				</Box>
			</Box>
		</Box>
	);
};

export default Giga;
